import React, { useState, useEffect } from "react";
import { z } from "zod";
import { Modal } from "@/components/common/Modal";
import { Input } from "@/components/common/Input";
import { Button } from "@/components/common/Button";
import {
  Application,
  CreateApplicationDTO,
  UpdateApplicationDTO,
} from "@/contracts/auth-api.types";
import { applicationService } from "@/services/applicationService";
import { useNotification } from "@/hooks/useNotification";

const expiryRegex = /^[0-9]+[smhd]$/;

const applicationSchema = z.object({
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres").max(100),
  client_id: z
    .string()
    .min(3, "client_id deve ter no mínimo 3 caracteres")
    .max(50)
    .regex(/^[a-z0-9-]+$/, "client_id deve conter apenas letras minúsculas, números e hífens"),
  description: z.string().max(255).optional(),
  frontend_url: z
    .string()
    .url("frontend_url deve ser uma URL válida (ex: https://app.dominio.com.br)"),
  cors_origins: z.string().optional(),
  access_token_expires_in: z
    .string()
    .regex(expiryRegex, "Tempo de expiração inválido (ex: 15m, 1h, 1d)")
    .optional()
    .or(z.literal("")),
  refresh_token_expires_in: z
    .string()
    .regex(expiryRegex, "Tempo de expiração inválido (ex: 7d, 30d)")
    .optional()
    .or(z.literal("")),
  client_secret: z
    .string()
    .min(8, "client_secret deve ter no mínimo 8 caracteres")
    .optional()
    .or(z.literal("")),
  is_active: z.boolean().default(true),
});

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  applicationToEdit?: Application | null;
}

export const ApplicationModal: React.FC<ApplicationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  applicationToEdit,
}) => {
  const { notify } = useNotification();
  const isEditing = !!applicationToEdit;

  const [formData, setFormData] = useState({
    name: "",
    client_id: "",
    description: "",
    frontend_url: "",
    cors_origins: "",
    access_token_expires_in: "15m",
    refresh_token_expires_in: "7d",
    client_secret: "",
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (applicationToEdit) {
      setFormData({
        name: applicationToEdit.name,
        client_id: applicationToEdit.client_id,
        description: applicationToEdit.description || "",
        frontend_url: applicationToEdit.frontend_url,
        cors_origins: (applicationToEdit.cors_origins || []).join(", "),
        access_token_expires_in: applicationToEdit.access_token_expires_in || "15m",
        refresh_token_expires_in: applicationToEdit.refresh_token_expires_in || "7d",
        client_secret: "",
        is_active: applicationToEdit.is_active,
      });
    } else {
      setFormData({
        name: "",
        client_id: "",
        description: "",
        frontend_url: "",
        cors_origins: "",
        access_token_expires_in: "15m",
        refresh_token_expires_in: "7d",
        client_secret: "",
        is_active: true,
      });
    }
    setErrors({});
  }, [applicationToEdit, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = applicationSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    const origins = formData.cors_origins
      ? formData.cors_origins
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    setIsSubmitting(true);
    try {
      if (isEditing && applicationToEdit) {
        const updatePayload: UpdateApplicationDTO = {
          name: formData.name,
          description: formData.description || null,
          frontend_url: formData.frontend_url,
          cors_origins: origins,
          access_token_expires_in: formData.access_token_expires_in || null,
          refresh_token_expires_in: formData.refresh_token_expires_in || null,
          is_active: formData.is_active,
        };
        if (formData.client_secret) {
          updatePayload.client_secret = formData.client_secret;
        }

        await applicationService.update(applicationToEdit.id, updatePayload);
        notify("Aplicação atualizada com sucesso!", "success");
      } else {
        const createPayload: CreateApplicationDTO = {
          name: formData.name,
          client_id: formData.client_id,
          description: formData.description,
          frontend_url: formData.frontend_url,
          cors_origins: origins,
          access_token_expires_in: formData.access_token_expires_in || "15m",
          refresh_token_expires_in: formData.refresh_token_expires_in || "7d",
          is_active: formData.is_active,
        };
        if (formData.client_secret) {
          createPayload.client_secret = formData.client_secret;
        }

        await applicationService.create(createPayload);
        notify(
          "Tenant application provisionado com sucesso com roles ADMIN e USER e 11 permissões operacionais padrão!",
          "success",
        );
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      notify(err.message || "Erro ao salvar a aplicação", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing
          ? `Editar Aplicação: ${applicationToEdit.name}`
          : "Registrar Nova Aplicação / Tenant"
      }
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="app-name-input"
            label="Nome da Aplicação"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            placeholder="Ex: Plataforma Clientes MVP"
            required
          />

          <Input
            id="app-client-id-input"
            label="Client ID Canônico"
            value={formData.client_id}
            onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
            error={errors.client_id}
            placeholder="ex: plataforma-clientes"
            helperText="Apenas minúsculas, números e hífens"
            disabled={isEditing}
            required
          />
        </div>

        <Input
          id="app-description-input"
          label="Descrição do Tenant"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          error={errors.description}
          placeholder="Ex: Tenant isolado para o produto de logística"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="app-frontend-url-input"
            label="URL do Frontend Principal"
            value={formData.frontend_url}
            onChange={(e) => setFormData({ ...formData, frontend_url: e.target.value })}
            error={errors.frontend_url}
            placeholder="https://painel.dominio.com.br"
            required
          />

          <Input
            id="app-cors-origins-input"
            label="Origens CORS Permitidas"
            value={formData.cors_origins}
            onChange={(e) => setFormData({ ...formData, cors_origins: e.target.value })}
            error={errors.cors_origins}
            placeholder="https://app.com, https://admin.com"
            helperText="Separe múltiplos domínios por vírgula"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="app-access-token-input"
            label="Expiração do Access Token"
            value={formData.access_token_expires_in}
            onChange={(e) => setFormData({ ...formData, access_token_expires_in: e.target.value })}
            error={errors.access_token_expires_in}
            placeholder="15m"
            helperText="Padrão: 15m (10m a 1h recomendado)"
          />

          <Input
            id="app-refresh-token-input"
            label="Expiração do Refresh Token"
            value={formData.refresh_token_expires_in}
            onChange={(e) => setFormData({ ...formData, refresh_token_expires_in: e.target.value })}
            error={errors.refresh_token_expires_in}
            placeholder="7d"
            helperText="Padrão: 7d (ex: 7d, 30d)"
          />
        </div>

        <Input
          id="app-client-secret-input"
          type="password"
          label="Client Secret (Opcional)"
          value={formData.client_secret}
          onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
          error={errors.client_secret}
          placeholder={
            isEditing ? "Deixe em branco para não alterar" : "Gere ou insira um segredo seguro"
          }
          helperText="Exigido apenas para clientes confidenciais (backends)"
        />

        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id="app-is-active-checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="rounded text-primary focus:ring-primary h-4 w-4"
          />
          <label htmlFor="app-is-active-checkbox" className="text-sm font-medium text-gray-700">
            Aplicação Ativa e Habilitada para Autenticação
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            {isEditing ? "Salvar Alterações" : "Criar Aplicação"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
